import { logger } from "../../utils/logger";
import axios, { AxiosHeaders } from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { env } from "../../config/env";
import { ErrorHandler } from "./errorHandler";
import { AuthManager } from "./authManager";

export class HttpClient {
  private client: AxiosInstance;
  private errorHandler: ErrorHandler;
  private authManager: AuthManager;

  constructor() {
    this.errorHandler = new ErrorHandler();

    // Create Axios instance with default configuration
    this.client = axios.create({
      baseURL: env.API_BASE_URL,
      timeout: env.API_TIMEOUT || 180000, // Increased to 3 minutes for AI operations
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Initialize auth manager
    this.authManager = new AuthManager(this.client, this.errorHandler);

    // Setup interceptors
    this.setupRequestInterceptors();
    this.setupResponseInterceptors();
  }

  /**
   * Get the configured Axios instance
   */
  getInstance(): AxiosInstance {
    return this.client;
  }

  /**
   * Get the error handler instance
   */
  getErrorHandler(): ErrorHandler {
    return this.errorHandler;
  }

  /**
   * Get the auth manager instance
   */
  getAuthManager(): AuthManager {
    return this.authManager;
  }

  /**
   * Setup request interceptors
   */
  private setupRequestInterceptors(): void {
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Check if data is FormData - if so, don't transform and remove Content-Type
        const isFormData = config.data instanceof FormData;

        // Add authentication if required
        if (this.authManager.requiresAuth(config)) {
          config = this.authManager.attachToken(
            config,
          ) as InternalAxiosRequestConfig;
        }

        // Add request ID for tracking
        if (!config.headers) {
          config.headers = new AxiosHeaders();
        }
        config.headers["X-Request-ID"] = this.generateRequestId();

        // For FormData, remove Content-Type to let browser set it with boundary
        if (isFormData && config.headers["Content-Type"]) {
          delete config.headers["Content-Type"];
        }

        // Log request in development
        if (import.meta.env?.DEV) {
          logger.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, {
            status: "request",
          });
        }

        return config;
      },
      (error) => {
        this.errorHandler.logError(
          this.errorHandler.handleError(error),
          "Request interceptor error",
        );
        return Promise.reject(this.errorHandler.handleError(error));
      },
    );
  }

  /**
   * Setup response interceptors
   */
  private setupResponseInterceptors(): void {
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response in development
        if (import.meta.env?.DEV) {
          logger.log(
            `✅ ${response.config.method?.toUpperCase()} ${response.config.url}`,
            { status: "request" },
          );
        }

        return response;
      },
      async (error) => {
        // Log error in development
        if (import.meta.env?.DEV) {
          logger.error(
            `❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
            { status: "request" },
          );
        }

        // Handle authentication failures
        if (error.response?.status === 401) {
          try {
            return await this.authManager.handleAuthFailure(
              error,
              error.config,
            );
          } catch (authError) {
            return Promise.reject(authError);
          }
        }

        // Handle other errors
        const apiError = this.errorHandler.handleError(error);
        this.errorHandler.logError(apiError, "Response interceptor error");

        return Promise.reject(apiError);
      },
    );
  }

  /**
   * Generate unique request ID for tracking
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Update client configuration
   */
  updateConfig(config: Partial<AxiosRequestConfig>): void {
    Object.assign(this.client.defaults, config);
  }

  /**
   * Set base URL
   */
  setBaseURL(baseURL: string): void {
    this.client.defaults.baseURL = baseURL;
  }

  /**
   * Set timeout
   */
  setTimeout(timeout: number): void {
    this.client.defaults.timeout = timeout;
  }

  /**
   * Add custom header
   */
  setHeader(name: string, value: string): void {
    this.client.defaults.headers.common[name] = value;
  }

  /**
   * Remove custom header
   */
  removeHeader(name: string): void {
    delete this.client.defaults.headers.common[name];
  }
}
